import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useAction, useMutation } from "convex/react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { ClipboardList, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "@/packages/utils";
import { springs } from "@/components/ui/motion";
import { SideSheet } from "@/components/ui/SideSheet";
import { Button } from "@/components/ui/primitives";
import { useAuditTemplates } from "@/hooks/use-audit-templates";
import { useAuditTags } from "@/hooks/use-audit-tags";
import { useCreateAudit } from "@/hooks/use-create-audit";
import { useWorkspaceAuditTypes } from "@/hooks/use-workspace-audit-types";
import { useWorkspaceId } from "@/hooks/use-workspace-mutation";
import { useWorkspace } from "@/routes/$workspaceSlug/route";
import { isCompleteOrgContext } from "@/components/workspace/OrgContextForm";
import { api } from "convex/_generated/api";
import { WizardStepIndicator } from "./WizardStepIndicator";
import { Step1DetailsForm } from "./Step1DetailsForm";
import { Step2Generating } from "./Step2Generating";
import { Step3ReviewChecklist } from "./Step3ReviewChecklist";
import { StepExtracting } from "./StepExtracting";
import { StepClauses } from "./StepClauses";
import { StepProfiling } from "./StepProfiling";
import { StepPiiRole } from "./StepPiiRole";
import { StepOrgContext } from "./StepOrgContext";
import { getDefaultFormValues } from "./wizardData";
import { handleCreditExhaustedError } from "@/components/ai/credit-exhausted-modal-store";
import { findProfileByCode } from "@/packages/standards";
import type { Id } from "@/packages/types";
import type { WorkspaceAuditTypeId } from "@/packages/types";
import type { UIAuditCategory, UIChecklistItem } from "@/packages/types";
import type {
  WizardStep,
  NewAuditForm,
  ChecklistSource,
  DepthTier,
  ProfilingAnswers,
  ProfilingState,
  PiiRole,
  OrgContextAnswers,
} from "./types";
import type { WorkspaceStandardOption } from "./AuditTypeSelector";

// ─── Session Storage Persistence ───────────────────────────────────────────
const FORM_STORAGE_KEY = "new-audit-wizard-form";
const WIZARD_STATE_STORAGE_KEY = "new-audit-wizard-state";

interface PersistedWizardState {
  step: WizardStep;
  auditId: string | null;
  userStandardId: string | null;
  sourceStandardId: string | null;
}

function loadPersistedForm(): NewAuditForm {
  const defaults = getDefaultFormValues();
  try {
    const raw = sessionStorage.getItem(FORM_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<NewAuditForm>;
      return {
        ...defaults,
        ...parsed,
        checklistSource: parsed.checklistSource ?? "official",
      };
    }
  } catch {
    // Ignore parse errors — fall back to defaults
  }
  return defaults;
}

function persistForm(form: NewAuditForm): void {
  try {
    sessionStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(form));
  } catch {
    // Ignore storage errors (e.g. quota exceeded)
  }
}

function loadPersistedWizardState(): PersistedWizardState | null {
  try {
    const raw = sessionStorage.getItem(WIZARD_STATE_STORAGE_KEY);
    if (raw) {
      return JSON.parse(raw) as PersistedWizardState;
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

function persistWizardState(state: PersistedWizardState): void {
  try {
    sessionStorage.setItem(WIZARD_STATE_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage errors
  }
}

function clearPersistedForm(): void {
  try {
    sessionStorage.removeItem(FORM_STORAGE_KEY);
    sessionStorage.removeItem(WIZARD_STATE_STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
}

interface NewAuditWizardProps {
  open: boolean;
  onClose: () => void;
}

const STEP_TITLES = {
  details: "audits:wizard.titles.details",
  piiRole: "audits:wizard.titles.piiRole",
  profiling: "audits:wizard.titles.profiling",
  orgContext: "audits:wizard.titles.orgContext",
  extracting: "audits:wizard.titles.extracting",
  clauses: "audits:wizard.titles.clauses",
  generating: "audits:wizard.titles.generating",
  review: "audits:wizard.titles.review",
} as const satisfies Record<WizardStep, string>;

const STEP_INDEX: Record<WizardStep, number> = {
  details: 0,
  piiRole: 1,
  profiling: 2,
  orgContext: 3,
  extracting: 4,
  clauses: 5,
  generating: 6,
  review: 7,
};

const stepVariants = {
  enter: (dir: number) => ({
    opacity: 0,
    x: dir * 40,
    scale: 0.98,
  }),
  center: {
    opacity: 1,
    x: 0,
    scale: 1,
  },
  exit: (dir: number) => ({
    opacity: 0,
    x: dir * -40,
    scale: 0.98,
  }),
};

export function NewAuditWizard({ open, onClose }: NewAuditWizardProps) {
  const { t } = useTranslation(["audits"]);
  const navigate = useNavigate();
  const { workspaceSlug } = useParams({ strict: false });
  const workspaceId = useWorkspaceId();
  const { workspace } = useWorkspace();
  const workspaceOrgContext = workspace.orgContext;
  const workspaceProfilingCompanySize = workspace.profilingCompanySize;
  const { templates } = useAuditTemplates();

  const { tags } = useAuditTags();
  const { createAudit, isCreating } = useCreateAudit();
  const { types: workspaceTypes } = useWorkspaceAuditTypes();

  // Fetch workspace standards for the AuditTypeSelector
  const { data: workspaceStandardsRaw } = useQuery(
    convexQuery(api.workspace_standards_queries.listReadyByWorkspace, {
      workspaceId,
    }),
  );
  const workspaceStandards: WorkspaceStandardOption[] = (
    workspaceStandardsRaw ?? []
  ).map((s) => ({
    _id: s._id,
    ready: s.ready,
    auditTypeName: s.auditTypeName,
    auditTypeCode: s.auditTypeCode,
    workspaceAuditTypeId: s.workspaceAuditTypeId
      ? String(s.workspaceAuditTypeId)
      : undefined,
  }));

  // Restore wizard state from sessionStorage on mount
  const persistedState = useRef(loadPersistedWizardState()).current;

  const [step, setStep] = useState<WizardStep>(
    persistedState?.step ?? "details",
  );
  const [form, setForm] = useState<NewAuditForm>(() => {
    const persisted = loadPersistedForm();
    return {
      ...persisted,
      orgContext: persisted.orgContext ?? workspaceOrgContext ?? null,
    };
  });
  const [auditId, setAuditId] = useState<Id<"audits"> | null>(
    persistedState?.auditId ? (persistedState.auditId as Id<"audits">) : null,
  );
  const [categories, setCategories] = useState<UIAuditCategory[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [direction, setDirection] = useState(1);
  const [fieldErrors, setFieldErrors] = useState<Set<string>>(new Set());
  const [validationAttempt, setValidationAttempt] = useState(0);
  const lastDeletedRef = useRef<{
    item: UIChecklistItem;
    categoryId: string;
    index: number;
  } | null>(null);

  // Upload workflow state
  const [userStandardId, setUserStandardId] =
    useState<Id<"officialStandards"> | null>(
      persistedState?.userStandardId
        ? (persistedState.userStandardId as Id<"officialStandards">)
        : null,
    );

  // Workspace standard selection state (selected from AuditTypeSelector)
  // Stored as string for sessionStorage persistence; cast on restore is safe
  // because the value originated from a Convex query _id field.
  const [sourceStandardId, setSourceStandardId] = useState<string | null>(
    persistedState?.sourceStandardId ?? null,
  );

  // Profiling state for depth tier selection — pre-fill company size from workspace default
  const [profilingState, setProfilingState] = useState<ProfilingState>({
    answers: workspaceProfilingCompanySize
      ? { companySize: workspaceProfilingCompanySize }
      : {},
    recommendedTier: null,
    selectedTier: null,
  });

  // Derive whether the orgContext step is part of this wizard session
  const hasOrgContext = useMemo(() => {
    const selectedTemplate = (templates ?? []).find(
      (tmpl) => tmpl.id === form.templateId,
    );
    const profile = selectedTemplate?.code
      ? findProfileByCode(selectedTemplate.code)
      : undefined;
    return (
      (profile?.annexes.length ?? 0) > 0 &&
      !isCompleteOrgContext(workspaceOrgContext ?? {})
    );
  }, [templates, form.templateId, workspaceOrgContext]);

  // Re-read persisted state when modal reopens (component may not remount)
  const prevOpenRef = useRef(open);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      const restored = loadPersistedWizardState();
      if (restored) {
        setStep(restored.step);
        setAuditId(
          restored.auditId ? (restored.auditId as Id<"audits">) : null,
        );
        setUserStandardId(
          restored.userStandardId
            ? (restored.userStandardId as Id<"officialStandards">)
            : null,
        );
        setSourceStandardId(restored.sourceStandardId ?? null);
      }
      const persisted = loadPersistedForm();
      setForm({
        ...persisted,
        orgContext: persisted.orgContext ?? workspaceOrgContext ?? null,
      });
    }
    prevOpenRef.current = open;
  }, [open, workspaceOrgContext]);
  const generateUserChecklist = useAction(
    api.user_standard_actions.generateUserChecklist,
  );
  const updateAuditStatus = useMutation(api.audit_mutations.updateAuditStatus);

  // Persist form values to sessionStorage whenever they change
  // and clear field errors when user edits the form
  // biome-ignore lint/correctness/useExhaustiveDependencies: fieldErrors intentionally omitted to avoid infinite loop (effect clears it)
  useEffect(() => {
    persistForm(form);
    if (fieldErrors.size > 0) setFieldErrors(new Set());
  }, [form]);

  // Persist wizard state (step, auditId, userStandardId, sourceStandardId) when past details step
  useEffect(() => {
    if (step !== "details") {
      persistWizardState({
        step,
        auditId: auditId ? String(auditId) : null,
        userStandardId: userStandardId ? String(userStandardId) : null,
        sourceStandardId,
      });
    }
  }, [step, auditId, userStandardId, sourceStandardId]);

  // Reset checklistSource when templateId or workspaceAuditTypeId changes
  const prevTemplateIdRef = useRef(form.templateId);
  const prevWsTypeIdRef = useRef(form.workspaceAuditTypeId);
  useEffect(() => {
    const templateChanged = form.templateId !== prevTemplateIdRef.current;
    const wsTypeChanged = form.workspaceAuditTypeId !== prevWsTypeIdRef.current;
    if (templateChanged || wsTypeChanged) {
      prevTemplateIdRef.current = form.templateId;
      prevWsTypeIdRef.current = form.workspaceAuditTypeId;
      // Only reset checklistSource if not set by workspace standard selection
      if (!sourceStandardId) {
        setForm((prev) => ({ ...prev, checklistSource: "official" }));
      }
    }
  }, [form.templateId, form.workspaceAuditTypeId, sourceStandardId]);

  const handleChecklistSourceChange = useCallback((source: ChecklistSource) => {
    setForm((prev) => ({ ...prev, checklistSource: source }));
  }, []);

  const handleUpdateChecklistItem = useCallback(
    (itemId: string, updates: Partial<UIChecklistItem>) => {
      setCategories((prev) =>
        prev.map((cat) => ({
          ...cat,
          items: cat.items.map((item) =>
            item.id === itemId ? { ...item, ...updates } : item,
          ),
        })),
      );
    },
    [],
  );

  const handleUndoDelete = useCallback(() => {
    const deleted = lastDeletedRef.current;
    if (!deleted) return;
    setCategories((prev) =>
      prev.map((cat) => {
        if (cat.id !== deleted.categoryId) return cat;
        const items = [...cat.items];
        items.splice(deleted.index, 0, deleted.item);
        return { ...cat, items };
      }),
    );
    lastDeletedRef.current = null;
  }, []);

  const handleDeleteChecklistItem = useCallback(
    (itemId: string) => {
      setCategories((prev) => {
        const newCategories = prev.map((cat) => {
          const idx = cat.items.findIndex((i) => i.id === itemId);
          if (idx === -1) return cat;
          lastDeletedRef.current = {
            item: cat.items[idx],
            categoryId: cat.id,
            index: idx,
          };
          return {
            ...cat,
            items: cat.items.filter((i) => i.id !== itemId),
          };
        });
        return newCategories;
      });
      toast(t("audits:wizard.actions.itemRemoved"), {
        action: {
          label: t("audits:wizard.actions.undo"),
          onClick: handleUndoDelete,
        },
      });
    },
    [handleUndoDelete, t],
  );

  const resetWizard = useCallback(() => {
    setStep("details");
    setForm(getDefaultFormValues());
    setAuditId(null);
    setCategories([]);
    setConfirming(false);
    setDirection(1);
    setUserStandardId(null);
    setSourceStandardId(null);
    setProfilingState({
      answers: {},
      recommendedTier: null,
      selectedTier: null,
    });
    clearPersistedForm();
  }, []);

  const handleClose = useCallback(() => {
    // Don't reset wizard state on close — let it persist for reopen.
    // State is only fully reset on confirm (successful completion).
    onClose();
  }, [onClose]);

  const goToStep = useCallback(
    (next: WizardStep) => {
      setDirection(STEP_INDEX[next] > STEP_INDEX[step] ? 1 : -1);
      setStep(next);
    },
    [step],
  );

  const handleDetailsNext = useCallback(() => {
    // Validate required fields
    const errors = new Set<string>();
    if (
      form.templateId === "" &&
      form.workspaceAuditTypeId === "" &&
      !sourceStandardId
    )
      errors.add("auditType");
    if (form.checklistSource === null) errors.add("checklistSource");
    if (form.displayName === "") errors.add("displayName");
    if (form.trigger === "") errors.add("trigger");
    if (form.deadline === "") errors.add("deadline");

    if (errors.size > 0) {
      setFieldErrors(errors);
      setValidationAttempt((n) => n + 1);
      return;
    }
    setFieldErrors(new Set());

    // Check if the selected standard requires PII role selection
    const selectedTemplate = (templates ?? []).find(
      (t) => t.id === form.templateId,
    );
    const templateCode = selectedTemplate?.code;
    const profile = templateCode ? findProfileByCode(templateCode) : undefined;

    if (profile?.hasRoleSelection) {
      goToStep("piiRole");
    } else {
      goToStep("profiling");
    }
  }, [form, sourceStandardId, goToStep, templates]);

  const handleGenerate = useCallback(
    async (
      depthTierOverride?: DepthTier,
      profilingAnswersOverride?: ProfilingAnswers,
    ) => {
      const effectiveDepthTier = depthTierOverride ?? form.depthTier;
      const effectiveProfilingAnswers =
        profilingAnswersOverride ?? form.profilingAnswers;

      const selectedTemplate = (templates ?? []).find(
        (t) => t.id === form.templateId,
      );
      // Guard: must have either a template, workspace audit type, or workspace standard selected
      if (!selectedTemplate && !form.workspaceAuditTypeId && !sourceStandardId)
        return;

      try {
        const newAuditId = await createAudit({
          name: form.displayName,
          templateCode: selectedTemplate?.code,
          templateId: selectedTemplate?.id,
          workspaceAuditTypeId: form.workspaceAuditTypeId
            ? (form.workspaceAuditTypeId as WorkspaceAuditTypeId)
            : undefined,
          description: form.description || undefined,
          deadline: form.deadline
            ? new Date(form.deadline).getTime()
            : undefined,
          trigger: form.trigger,
          tagIds:
            form.tagIds.length > 0
              ? (form.tagIds as Id<"auditTags">[])
              : undefined,
          notes: form.notes || undefined,
          auditorRequestText: form.auditorRequestText || undefined,
          checklistSource: form.checklistSource ?? "ai_research",
          uploadedDocumentStorageId: undefined,
          // sourceStandardId originates from Convex query _id, safe to pass as branded type
          sourceStandardId: sourceStandardId
            ? (sourceStandardId as Id<"officialStandards">)
            : undefined,
          depthTier: effectiveDepthTier ?? undefined,
          profilingAnswers: effectiveProfilingAnswers ?? undefined,
          piiRole: form.piiRole ?? undefined,
          orgContext: form.orgContext ?? undefined,
        });
        setAuditId(newAuditId);

        // Also store in form for persistence
        if (depthTierOverride || profilingAnswersOverride) {
          setForm((prev) => ({
            ...prev,
            depthTier: effectiveDepthTier,
            profilingAnswers: effectiveProfilingAnswers,
          }));
        }

        // For "upload" source without a workspace standard, advance to extracting step
        if (form.checklistSource === "upload" && !sourceStandardId) {
          goToStep("extracting");
          return;
        }

        // For workspace standard source (checklistSource is auto-set to "upload" with sourceStandardId),
        // the backend materializes the checklist and sets status to "ready".
        // Go to "generating" step which will auto-advance to review since status is "ready".
        goToStep("generating");
      } catch {
        // Error display is handled by useApiMutation via showErrorToast / global interceptors
      }
    },
    [form, templates, createAudit, goToStep, sourceStandardId],
  );

  const handleChecklistReady = useCallback(
    (readyCategories: UIAuditCategory[]) => {
      setCategories(readyCategories);
      goToStep("review");
    },
    [goToStep],
  );

  const handleConfirm = useCallback(async () => {
    if (!auditId) return;
    setConfirming(true);

    // Transition audit status from "pending" to "in_progress"
    try {
      await updateAuditStatus({
        auditId,
        status: "in_progress",
      });
    } catch {
      // Non-blocking: if status update fails, still navigate to audit
    }

    setTimeout(() => {
      navigate({
        to: "/$workspaceSlug/audits/$auditId",
        params: {
          workspaceSlug: workspaceSlug ?? "",
          auditId: `${auditId}`,
        },
      });
      // On successful creation, reset wizard and clear persisted form
      resetWizard();
      onClose();
    }, 600);
  }, [
    auditId,
    navigate,
    resetWizard,
    onClose,
    workspaceSlug,
    updateAuditStatus,
  ]);

  const handleBack = useCallback(() => {
    goToStep(hasOrgContext ? "orgContext" : "profiling");
  }, [goToStep, hasOrgContext]);

  return (
    <SideSheet open={open} onClose={handleClose}>
      <SideSheet.Header
        title={t(STEP_TITLES[step])}
        icon={<ClipboardList size={20} />}
      />

      <SideSheet.Body>
        <WizardStepIndicator
          currentStep={step}
          checklistSource={form.checklistSource}
          hasRoleSelection={(() => {
            const selectedTemplate = (templates ?? []).find(
              (tmpl) => tmpl.id === form.templateId,
            );
            const templateCode = selectedTemplate?.code;
            return templateCode
              ? (findProfileByCode(templateCode)?.hasRoleSelection ?? false)
              : false;
          })()}
          hasOrgContext={hasOrgContext}
        />

        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={springs.smooth}
          >
            {step === "details" && (
              <>
                {/* Validation banner */}
                <AnimatePresence>
                  {fieldErrors.size > 0 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="mb-3"
                    >
                      <div className="flex items-center gap-2 rounded-lg bg-red-500/10 border border-red-500/20 px-3 py-2">
                        <TriangleAlert
                          size={14}
                          className="shrink-0 text-red-500"
                        />
                        <span className="text-xs font-medium text-red-500">
                          {t("audits:wizard.errors.validationBanner", {
                            count: fieldErrors.size,
                          })}
                        </span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <Step1DetailsForm
                  form={form}
                  onChange={setForm}
                  onChecklistSourceChange={handleChecklistSourceChange}
                  templates={templates ?? []}
                  tags={tags ?? []}
                  workspaceTypes={workspaceTypes ?? []}
                  workspaceStandards={workspaceStandards}
                  fieldErrors={fieldErrors}
                  validationAttempt={validationAttempt}
                  sourceStandardId={sourceStandardId}
                  onSourceStandardIdChange={setSourceStandardId}
                />
              </>
            )}
            {step === "piiRole" && (
              <StepPiiRole
                selectedRole={form.piiRole}
                onSelect={(role: PiiRole) => {
                  setForm((prev) => ({ ...prev, piiRole: role }));
                }}
                onNext={() => goToStep("profiling")}
                onBack={() => goToStep("details")}
              />
            )}
            {step === "profiling" && (
              <StepProfiling
                profilingState={profilingState}
                onStateChange={setProfilingState}
              />
            )}
            {step === "orgContext" && (
              <StepOrgContext
                answers={form.orgContext ?? {}}
                onAnswersChange={(answers) => {
                  setForm((prev) => ({
                    ...prev,
                    orgContext: answers as OrgContextAnswers,
                  }));
                }}
                onComplete={(orgContextAnswers) => {
                  setForm((prev) => ({
                    ...prev,
                    orgContext: orgContextAnswers,
                  }));
                  handleGenerate();
                }}
                loading={isCreating}
              />
            )}
            {step === "extracting" && auditId && (
              <StepExtracting
                auditId={auditId}
                onExtractionComplete={(standardId) => {
                  setUserStandardId(standardId);
                  goToStep("clauses");
                }}
                onError={(error) => toast.error(error)}
              />
            )}
            {step === "clauses" && userStandardId && auditId && (
              <StepClauses standardId={userStandardId} auditId={auditId} />
            )}
            {step === "generating" && auditId && (
              <Step2Generating
                auditId={auditId}
                auditName={form.displayName}
                onChecklistReady={handleChecklistReady}
                onCancel={handleClose}
              />
            )}
            {step === "review" && (
              <Step3ReviewChecklist
                categories={categories}
                auditName={form.displayName}
                onUpdateItem={handleUpdateChecklistItem}
                onDeleteItem={handleDeleteChecklistItem}
                checklistSource={form.checklistSource}
                userStandardId={userStandardId}
                auditId={auditId}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </SideSheet.Body>

      <SideSheet.Footer>
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            className="flex w-full items-center justify-end gap-3"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={springs.snappy}
          >
            {step === "details" && (
              <>
                <Button variant="secondary" onClick={handleClose}>
                  {t("audits:wizard.actions.cancel")}
                </Button>
                <Button variant="primary" onClick={handleDetailsNext}>
                  {t("audits:wizard.actions.nextProfiling")}
                </Button>
              </>
            )}
            {step === "piiRole" && null}
            {step === "profiling" && (
              <>
                <Button
                  variant="secondary"
                  onClick={() => {
                    const selectedTemplate = (templates ?? []).find(
                      (tmpl) => tmpl.id === form.templateId,
                    );
                    const templateCode = selectedTemplate?.code;
                    const profile = templateCode
                      ? findProfileByCode(templateCode)
                      : undefined;
                    goToStep(profile?.hasRoleSelection ? "piiRole" : "details");
                  }}
                >
                  {t("audits:wizard.actions.backToDetails")}
                </Button>
                <Button
                  variant="primary"
                  disabled={
                    !profilingState.selectedTier ||
                    profilingState.answers.companySize === undefined ||
                    profilingState.answers.maturity === undefined ||
                    profilingState.answers.goal === undefined
                  }
                  loading={isCreating}
                  onClick={() => {
                    const { selectedTier, answers } = profilingState;
                    if (
                      selectedTier &&
                      answers.companySize !== undefined &&
                      answers.maturity !== undefined &&
                      answers.goal !== undefined
                    ) {
                      const completeAnswers: ProfilingAnswers = {
                        companySize: answers.companySize,
                        maturity: answers.maturity,
                        goal: answers.goal,
                      };
                      setForm((prev) => ({
                        ...prev,
                        depthTier: selectedTier,
                        profilingAnswers: completeAnswers,
                      }));
                      if (hasOrgContext) {
                        goToStep("orgContext");
                      } else {
                        handleGenerate(selectedTier, completeAnswers);
                      }
                    }
                  }}
                >
                  {t("audits:wizard.actions.nextGenerateChecklist")}
                </Button>
              </>
            )}
            {step === "orgContext" && (
              <Button variant="secondary" onClick={() => goToStep("profiling")}>
                {t("audits:wizard.actions.back")}
              </Button>
            )}
            {step === "extracting" && (
              <Button variant="secondary" onClick={handleClose}>
                {t("audits:wizard.actions.cancel")}
              </Button>
            )}
            {step === "clauses" && userStandardId && (
              <>
                <Button variant="secondary" onClick={handleClose}>
                  {t("audits:wizard.actions.cancel")}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setUserStandardId(null);
                    goToStep("extracting");
                  }}
                >
                  {t("audits:wizard.actions.reExtract")}
                </Button>
                <Button
                  variant="primary"
                  onClick={async () => {
                    goToStep("generating");
                    try {
                      await generateUserChecklist({
                        standardId: userStandardId,
                      });
                    } catch (error) {
                      if (!handleCreditExhaustedError(error)) {
                        toast.error(
                          t("audits:wizard.actions.failedGenerateChecklist"),
                        );
                      }
                    }
                    goToStep("clauses");
                  }}
                >
                  {t("audits:wizard.actions.approveGenerateChecklist")}
                </Button>
              </>
            )}
            {step === "generating" && (
              <>
                <Button variant="secondary" onClick={handleClose}>
                  {t("audits:wizard.actions.cancel")}
                </Button>
                <Button variant="primary" loading disabled>
                  {t("audits:wizard.actions.working")}
                </Button>
              </>
            )}
            {step === "review" && (
              <>
                {form.checklistSource !== "upload" && (
                  <Button variant="secondary" onClick={handleBack}>
                    {t("audits:wizard.actions.backToDetails")}
                  </Button>
                )}
                <Button
                  variant="primary"
                  onClick={handleConfirm}
                  loading={confirming}
                >
                  {t("audits:wizard.actions.confirmOpenAudit")}
                </Button>
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </SideSheet.Footer>
    </SideSheet>
  );
}
